/**
 * Level repository: pack index + one lazily-loaded JSON per level. Loaders are injected
 * (Vite import.meta.glob in the app, fixtures in tests) so this module stays platform-free.
 */
import { RULES_VERSION, SUPPORTED_MECHANICS, unsupportedMechanics, validateLevel, type LevelV1, type Mechanic } from '@mp/core';

export interface PackEntry {
  id: string;
  number: number;
  shop: string;
  hash: string;
  mechanics: Mechanic[];
  par: number;
}

export interface Pack {
  city: string;
  version: number;
  levels: PackEntry[];
}

type Loader<T> = () => Promise<T | { default: T }>;

/**
 * Roadmap-3k §8.1 gate: content is playable by this build only when its engine plays everything
 * the content needs: rules `minRulesVersion` (RULES_VERSION at least that new) and every mechanic
 * in `mechanics` (core SUPPORTED_MECHANICS). All ten roadmap mechanics ship on rules v2, so the
 * version alone cannot tell; a pack manifest and a level also list their mechanics, and one this
 * engine lacks needs the update too. Otherwise the player sees "update the app".
 */
export function needsAppUpdate(minRulesVersion: number, mechanics: readonly string[] = [], engine: number = RULES_VERSION, supported: readonly string[] = SUPPORTED_MECHANICS): boolean {
  return minRulesVersion > engine || mechanics.some((m) => !supported.includes(m));
}

/** A level or city pack that needs a newer app: its rules version is above RULES_VERSION, or it uses a mechanic this engine lacks. */
export class NeedsUpdateError extends Error {
  /** the content's mechanics this engine does not play (empty when the rules version is the reason) */
  readonly missing: string[];

  constructor(
    readonly city: string,
    readonly rulesVersion: number,
    mechanics: readonly string[] = [],
  ) {
    const missing = unsupportedMechanics(mechanics);
    super(missing.length ? `${city} uses ${missing.join(', ')}, which this app does not play` : `${city} needs rules v${rulesVersion}; this app plays up to v${RULES_VERSION}`);
    this.missing = missing;
  }
}

export interface LevelSources {
  packs: Record<string, Loader<Pack>>;
  levels: Record<string, Loader<LevelV1>>;
  /** dev-only in-memory generator used when a city has no pack yet */
  dev?: { count: number; generate: (city: string, n: number) => Promise<LevelV1> };
}

function unwrap<T>(m: T | { default: T }): T {
  return m && typeof m === 'object' && 'default' in m ? (m as { default: T }).default : (m as T);
}

export class LevelRepo {
  private packs = new Map<string, Pack | null>();
  private cache = new Map<string, Promise<LevelV1>>();
  /** LO-02 city packs registered at runtime: city → level-file loader */
  private external = new Map<string, (id: string) => Promise<LevelV1>>();
  /** city → the pack's own update check (a newer version registers itself again) */
  private refreshers = new Map<string, () => Promise<void>>();
  /** minRulesVersion of each registered pack (manifest): above RULES_VERSION, the city stays locked */
  private minRules = new Map<string, number>();
  /** mechanics each registered pack's levels use (manifest): one this engine lacks keeps the city locked too */
  private mechanics = new Map<string, readonly string[]>();
  private listeners = new Set<(city: string) => void>();

  constructor(private src: LevelSources) {}

  /**
   * LO-02 extension point: a downloaded city pack (its pack index and how to load one of its
   * level files). The city then plays like a built-in one; its street joins the map's journey.
   * Registering again (a new pack version) replaces the old one. `refresh` looks for a newer
   * version of the pack (used when the server says a level is outdated). A pack whose
   * `minRulesVersion` is above RULES_VERSION, or whose `mechanics` name one this engine lacks,
   * still shows on the map (a teaser: update the app), but none of its levels load (`needsUpdate`).
   */
  registerPack(
    city: string,
    pack: Pack,
    loadLevel: (id: string) => Promise<LevelV1>,
    o: { refresh?: () => Promise<void>; minRulesVersion?: number; mechanics?: readonly string[] } = {},
  ): void {
    const p: Pack = { ...pack, city, levels: [...pack.levels].sort((a, b) => a.number - b.number) };
    this.packs.set(city, p);
    this.external.set(city, loadLevel);
    if (o.refresh) this.refreshers.set(city, o.refresh);
    this.minRules.set(city, o.minRulesVersion ?? 1);
    this.mechanics.set(city, o.mechanics ?? []);
    for (const k of [...this.cache.keys()]) if (k.startsWith(`${city}:`)) this.cache.delete(k);
    for (const fn of this.listeners) fn(city);
  }

  /** The registered pack city a level belongs to (null for built-in levels). */
  private packCityOf(levelId: string): string | null {
    for (const city of this.external.keys()) if (this.packs.get(city)?.levels.some((e) => e.id === levelId)) return city;
    return null;
  }

  /** The level comes from a pack fetched at runtime (a city pack), so a newer version can be fetched without an app update. */
  runtime(levelId: string): boolean {
    return this.packCityOf(levelId) !== null;
  }

  /**
   * The server plays a newer version of a runtime level (level_outdated): let its pack look
   * for an update, and forget the cached copy so the next play loads the level again.
   * False for built-in levels (only an app update brings those).
   */
  async refresh(levelId: string): Promise<boolean> {
    const city = this.packCityOf(levelId);
    if (!city) return false;
    const drop = () => {
      const n = this.packs.get(city)?.levels.find((e) => e.id === levelId)?.number;
      if (n !== undefined) this.cache.delete(`${city}:${n}`);
    };
    drop();
    await this.refreshers.get(city)?.();
    drop();
    return true;
  }

  /** Cities added through registerPack, in registration order. */
  externalCities(): string[] {
    return [...this.external.keys()];
  }

  /** True when the city's pack needs a newer app: the map shows "update the app" instead of entering it. */
  needsUpdate(city: string): boolean {
    return needsAppUpdate(this.minRules.get(city) ?? 1, this.mechanics.get(city));
  }

  /** Called with the city whenever a pack is registered. */
  onChange(fn: (city: string) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** First and last level number of a city (packs may number from 301, e.g. Paris). */
  range(city: string): { first: number; last: number } {
    const pack = this.packs.get(city);
    if (pack?.levels.length) return { first: pack.levels[0]!.number, last: pack.levels[pack.levels.length - 1]!.number };
    return { first: 1, last: this.src.dev?.count ?? 0 };
  }

  /** Load the city pack index (null when missing: dev fallback or none). */
  async loadPack(city: string): Promise<Pack | null> {
    if (this.packs.has(city)) return this.packs.get(city)!;
    const loader = this.src.packs[city];
    const pack = loader ? unwrap(await loader()) : null;
    if (pack) pack.levels.sort((a, b) => a.number - b.number);
    this.packs.set(city, pack);
    return pack;
  }

  usingDevLevels(city: string): boolean {
    return !this.packs.get(city) && !!this.src.dev;
  }

  count(city: string): number {
    const pack = this.packs.get(city);
    if (pack) return pack.levels.length;
    return this.src.dev?.count ?? 0;
  }

  entry(city: string, n: number): PackEntry | undefined {
    return this.packs.get(city)?.levels.find((l) => l.number === n);
  }

  load(city: string, n: number): Promise<LevelV1> {
    const key = `${city}:${n}`;
    let p = this.cache.get(key);
    if (!p) {
      p = this.fetchLevel(city, n);
      this.cache.set(key, p);
      p.catch(() => this.cache.delete(key));
    }
    return p;
  }

  /** Warm the next level while the player is busy (non-blocking). */
  prefetch(city: string, n: number): void {
    const { first, last } = this.range(city);
    if (n >= first && n <= last) void this.load(city, n).catch(() => undefined);
  }

  private async fetchLevel(city: string, n: number): Promise<LevelV1> {
    if (this.needsUpdate(city)) throw new NeedsUpdateError(city, this.minRules.get(city)!, this.mechanics.get(city));
    const pack = this.packs.get(city) ?? (await this.loadPack(city));
    let level: LevelV1;
    if (pack) {
      const e = pack.levels.find((l) => l.number === n);
      if (!e) throw new Error(`level ${city} #${n} is not in the pack`);
      const ext = this.external.get(city);
      const loader = this.src.levels[e.id];
      if (ext) level = await ext(e.id);
      else if (loader) level = unwrap(await loader());
      else throw new Error(`level file ${e.id} is missing`);
    } else if (this.src.dev) {
      level = await this.src.dev.generate(city, n);
    } else throw new Error(`no levels for ${city}`);
    // a level file newer than this engine (a stale pack index, a shared link): refused, never played
    if (needsAppUpdate(level.rulesVersion, level.mechanics)) throw new NeedsUpdateError(city, level.rulesVersion, level.mechanics);
    const errs = validateLevel(level);
    if (errs.length) throw new Error(`level ${level.id} is invalid: ${errs.slice(0, 3).join('; ')}`);
    return level;
  }
}
